import { computed, type ComputedRef } from 'vue'

import { resolvePortalLogo } from '@clientportal/auth-layer/utils/branding-assets'
import { useBrandedAppMetadata } from '@clientportal/legal-layer/composables/useBrandedAppMetadata'
import { resolveBrandedAppStoreLinks } from '@clientportal/legal-layer/utils/branded-app-store-links'

import { getConfig } from '@common/core-layer/config'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLocation } from '@common/location-layer/composables/useLocation'

/**
 * "Open app" banner context for CPRootContainer: the location's branded-app
 * store links and icon. Only locations listed in the environment's
 * `CLIENTPORTAL_APP_BANNER_LOCATION_IDS` get the banner, and metadata is only
 * requested for them on mobile/tablet clients, the only place it renders.
 *
 * @param suppressed - routes that own a fixed bottom action bar the banner would cover
 */
export function useCPAppOpenBanner(suppressed: ComputedRef<boolean>) {
  const { locationId, store: locationStore } = useLocation()
  const { isMobileOrTablet } = useGlobal()

  const isAllowedLocation = computed(() => getConfig().CLIENTPORTAL_APP_BANNER_LOCATION_IDS.includes(locationId.value ?? ''))
  const enabled = computed(() => import.meta.client && isAllowedLocation.value && isMobileOrTablet())
  const { data: metadata } = useBrandedAppMetadata().fetchBrandedAppMetadataQuery(locationId, enabled)

  const storeLinks = computed(() => resolveBrandedAppStoreLinks(metadata.value))
  // App icon first, then the portal logo; '' hides the banner's icon tile.
  const logoSrc = computed(() => metadata.value?.appDetails?.appIconImage || resolvePortalLogo(locationStore.branding) || '')
  const shouldShow = computed(() => isAllowedLocation.value && storeLinks.value !== null && !suppressed.value)

  return { storeLinks, logoSrc, shouldShow }
}
